import { z } from 'zod'

export const LabelErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  path: z.array(z.string()).optional(),
  suggestion: z.string().optional(),
  recoverable: z.boolean(),
  retryAfterMs: z.number().int().nonnegative().optional(),
})

export type LabelError = z.infer<typeof LabelErrorSchema>

export const ResultSchema = <T extends z.ZodTypeAny>(data: T) =>
  z.discriminatedUnion('status', [
    z.object({
      status: z.literal('ok'),
      data,
      etag: z.string().optional(),
      cursor: z.string().optional(),
    }),
    z.object({
      status: z.literal('error'),
      error: LabelErrorSchema,
    }),
  ])

export type Result<T> =
  | { status: 'ok'; data: T; etag?: string; cursor?: string }
  | { status: 'error'; error: LabelError }

export type OkExtras = { etag?: string; cursor?: string }

export function ok<T>(data: T, extras?: OkExtras): Result<T> {
  return { status: 'ok', data, ...extras }
}

export type ErrOptions = Partial<Omit<LabelError, 'code' | 'message'>>

export function err(code: string, message: string, opts?: ErrOptions): Result<never> {
  return {
    status: 'error',
    error: { code, message, recoverable: opts?.recoverable ?? false, ...opts },
  }
}
